import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { saveRPE } from '../../api';
import { rpeValueColor, rpeAssessment } from '../WellnessInput';
import { usePlayerData } from './PlayerDataContext';

// LOCAL calendar date — toISOString() is UTC, which files a post-midnight rating (00:00–02:00
// in CEST) against yesterday while the rest of the player app already reads it as today.
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const MUSCULAR_STEP = {
  key: 'muscular',
  title: 'How hard was the muscular effort?',
  subtitle: '1 = Very light  ·  10 = Maximum exertion',
  color: '#f59e0b',
};
const CARDIO_STEP = {
  key: 'cardio',
  title: 'How heavy did the session feel on your lungs and heart?',
  subtitle: '1 = Very light  ·  10 = Maximum exertion',
  color: '#3b82f6',
};

// Gym RPE (2026-08-17) asks the muscular question only — a strength session has no meaningful
// cardiovascular rating, so that step is dropped rather than asked-and-ignored, and the row is
// saved with rpe_cardiovascular NULL (see the POST /api/rpe comment).
const STEPS_BY_TYPE = {
  pitch: [MUSCULAR_STEP, CARDIO_STEP],
  gym:   [MUSCULAR_STEP],
};

function ScaleGrid({ value, onChange, color }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
      {[1,2,3,4,5,6,7,8,9].map(n => {
        const btnColor = rpeValueColor(n);
        const selected = value === n;
        return (
          <button key={n} onClick={() => onChange(n === value ? null : n)}
            style={{
              height: 72, borderRadius: 14, fontFamily: 'inherit',
              border: `2px solid ${selected ? btnColor : 'var(--border-strong)'}`,
              background: selected ? btnColor : 'var(--bg-card)',
              color: selected ? (btnColor === '#facc15' ? '#111' : '#fff') : 'var(--text-secondary)',
              fontSize: 26, fontWeight: 700, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >{n}</button>
        );
      })}
      {(() => {
        const btnColor = rpeValueColor(10);
        const selected = value === 10;
        return (
          <button onClick={() => onChange(10 === value ? null : 10)}
            style={{
              height: 72, borderRadius: 14, fontFamily: 'inherit',
              border: `2px solid ${selected ? btnColor : 'var(--border-strong)'}`,
              background: selected ? btnColor : 'var(--bg-card)',
              color: selected ? '#fff' : 'var(--text-secondary)',
              fontSize: 26, fontWeight: 700, cursor: 'pointer',
              gridColumn: '2',
              transition: 'all 0.15s',
            }}
          >10</button>
        );
      })()}
    </div>
  );
}

// `date` is only set for the late-game "Fill in game RPE" flow (see PlayerWellness) — the
// rating is then filed against the match date instead of today.
export default function MobileRPEWizard({ playerRecord, sessionType = 'pitch', date = null }) {
  const navigate = useNavigate();
  const { refresh } = usePlayerData();
  const [step, setStep] = useState(0);
  const [muscular, setMuscular] = useState(null);
  const [cardio, setCardio] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const STEPS = STEPS_BY_TYPE[sessionType] || STEPS_BY_TYPE.pitch;
  const total = STEPS.length;
  const cfg = STEPS[step];
  const isCardioStep = cfg.key === 'cardio';
  const currentValue = isCardioStep ? cardio : muscular;
  const setCurrentValue = isCardioStep ? setCardio : setMuscular;

  const handleNext = async () => {
    if (step < total - 1) {
      setStep(s => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      await handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!playerRecord) return;
    setSaving(true);
    try {
      await saveRPE({
        player_id:          parseInt(playerRecord.id),
        date:               date || today(),
        rpe_muscular:       muscular,
        rpe_cardiovascular: sessionType === 'gym' ? null : cardio,
        session_type:       sessionType,
      });
      await refresh();
      setSubmitted(true);
    } catch {
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 20, padding: 32, textAlign: 'center', background: 'var(--bg-app)' }}>
        <CheckCircle size={72} color="#10b981" strokeWidth={1.5} />
        <h2 style={{ fontSize: 24, fontWeight: 700, color: '#10b981', margin: 0 }}>Saved!</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 15, margin: 0 }}>Your {date ? 'game' : sessionType === 'gym' ? 'gym' : 'pitch'} RPE has been recorded.</p>
        <button onClick={() => navigate('/player/forms')}
          style={{ marginTop: 12, padding: '14px 32px', borderRadius: 12, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          Done
        </button>
      </div>
    );
  }

  const isLast = step === total - 1;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-app)', color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif' }}>
      {/* Top bar */}
      <div style={{ padding: '16px 24px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <button onClick={() => step === 0 ? navigate('/player/forms') : setStep(s => s - 1)}
            style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 15, fontWeight: 600, padding: 0, fontFamily: 'inherit' }}>
            ← {step === 0 ? 'Back' : 'Previous'}
          </button>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Step {step + 1} of {total}</span>
        </div>
        {/* Progress bar */}
        <div style={{ height: 4, background: 'var(--border)', borderRadius: 2 }}>
          <div style={{
            height: 4,
            width: `${((step + 1) / total) * 100}%`,
            background: cfg.color,
            borderRadius: 2,
            transition: 'width 0.3s ease',
          }} />
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '28px 24px 140px' }}>
        {date && (
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', color: '#f59e0b', marginBottom: 8 }}>
            LAST NIGHT'S GAME · {new Date(date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }).toUpperCase()}
          </div>
        )}
        <h2 style={{ fontSize: 26, fontWeight: 700, marginBottom: 6, lineHeight: 1.2 }}>
          {cfg.title}
        </h2>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 28 }}>
          {cfg.subtitle}
        </p>

        <ScaleGrid value={currentValue} onChange={setCurrentValue} color={cfg.color} />

        {currentValue && (
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <div style={{ fontSize: 40, fontWeight: 900, color: rpeValueColor(currentValue), lineHeight: 1 }}>
              {currentValue}
            </div>
            <div style={{ fontSize: 15, color: 'var(--text-muted)', marginTop: 6, fontWeight: 600 }}>
              {rpeAssessment(currentValue)}
            </div>
          </div>
        )}

        {/* RPE reference */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginTop: 32 }}>
          {[['1–2','Very light'],['3–4','Light'],['5–6','Moderate'],['7–8','Hard'],['9–10','Max']].map(([range, lbl]) => (
            <div key={range} style={{ background: 'var(--bg-card)', borderRadius: 8, padding: '6px 4px', textAlign: 'center', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: cfg.color }}>{range}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Fixed bottom CTA */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        padding: '16px 24px max(16px, env(safe-area-inset-bottom))',
        background: 'var(--bg-app)',
        borderTop: '1px solid var(--border)',
      }}>
        <button onClick={handleNext} disabled={saving || !currentValue}
          style={{
            width: '100%', height: 52, borderRadius: 12, border: 'none',
            background: isLast ? '#10b981' : cfg.color,
            color: '#fff', fontSize: 16, fontWeight: 700,
            cursor: (!currentValue || saving) ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            opacity: (!currentValue || saving) ? 0.5 : 1,
          }}>
          {saving ? 'Saving…' : isLast ? '✓ Submit' : 'Next →'}
        </button>
      </div>
    </div>
  );
}
